import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import OutboundIcon from '@mui/icons-material/OutboundOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShippingOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmptyOutlined';
import { apiClient } from '@/api/axiosClient';
import StatCard from '@/components/common/StatCard';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardLineChart from '@/components/dashboard/DashboardLineChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import { groupCountBy, groupSumByDay } from '@/lib/dashboardAggregations';

const TREND_DAYS = 15;
import { withId } from '@/pages/reports/reportHelpers';
import { usePortalCategoryColors } from '../portalColors';
import { isWithinDateRange } from '@/lib/dateRangeFilter';
import PortalDateRangeFilter from '../PortalDateRangeFilter';
import type { DispatchOrderRegisterRow } from '@/types/reports';

const STATUS_SLICE_COLORS = ['#0566d9', '#ffb951', '#4edea3', '#ba1a1a', '#6b6f76'];

export default function DispatchPanel() {
  const { t } = useTranslation('clientPortal');
  const colors = usePortalCategoryColors();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['client-portal', 'dispatch-order-register'],
    queryFn: async () => (await apiClient.get<DispatchOrderRegisterRow[]>('/client-portal/dispatch-order-register')).data,
  });
  // Date-range filter only narrows the table below — the stat cards/trend chart above keep
  // summarizing the full register (the chart already has its own 15-day window).
  const tableRows = useMemo(() => rows.filter((r) => isWithinDateRange(r.orderDate, fromDate, toDate)), [rows, fromDate, toDate]);

  const totalOrders = useMemo(() => new Set(rows.map((r) => r.dispatchOrderId)).size, [rows]);
  const issuedQty = useMemo(() => rows.reduce((sum, r) => sum + r.issuedQty, 0), [rows]);
  const pendingQty = useMemo(() => rows.reduce((sum, r) => sum + r.pendingQty, 0), [rows]);

  const statusDistribution = useMemo(() => groupCountBy(rows, (r) => r.status), [rows]);
  // Dispatch is the one category the portal tracks day-by-day rather than month-by-month — outbound
  // activity is the thing a Client is most likely watching closely in the short term.
  const dailyTrend = useMemo(
    () => groupSumByDay(rows, (r) => r.orderDate, [
      { key: 'count', value: () => 1 },
      { key: 'issuedQty', value: (r) => r.issuedQty },
    ], TREND_DAYS),
    [rows],
  );
  const hasRecentActivity = dailyTrend.some((d) => d.values.count > 0);

  const columns: DataTableColumn<DispatchOrderRegisterRow>[] = [
    { field: 'orderNumber', headerName: t('dispatchOrdersPage.table.orderNumber') },
    { field: 'orderDate', headerName: t('dispatchOrdersPage.table.orderDate'), render: (r) => new Date(r.orderDate).toLocaleDateString() },
    { field: 'customerName', headerName: t('dispatchOrdersPage.table.customerName'), filterable: true },
    { field: 'materialName', headerName: t('dispatchOrdersPage.table.materialName'), filterable: true },
    { field: 'issuedQty', headerName: t('dispatchOrdersPage.table.issuedQty'), align: 'right' },
    { field: 'status', headerName: t('dispatchOrdersPage.table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
  ];

  return (
    <Stack spacing={3}>
      <GridContainer columns={12}>
        <GridItem xs={12} sm={4}>
          <StatCard label={t('dispatchPanel.totalOrders')} value={totalOrders} icon={<OutboundIcon />} accentColor={colors.dispatch} />
        </GridItem>
        <GridItem xs={12} sm={4}>
          <StatCard label={t('dispatchPanel.issuedQty')} value={issuedQty.toLocaleString()} icon={<LocalShippingIcon />} accentColor={colors.overview} />
        </GridItem>
        <GridItem xs={12} sm={4}>
          <StatCard label={t('dispatchPanel.pendingQty')} value={pendingQty.toLocaleString()} icon={<HourglassEmptyIcon />} accentColor={colors.invoices} />
        </GridItem>
      </GridContainer>

      <GridContainer columns={12} spacing={2}>
        <GridItem xs={12} md={7}>
          <ChartCard title={t('dispatchPanel.trendTitle')} subtitle={t('dispatchPanel.trendSubtitle')} loading={isLoading} empty={!isLoading && !hasRecentActivity}>
            <DashboardLineChart
              categories={dailyTrend.map((d) => d.day)}
              series={[
                { label: t('dispatchPanel.ordersSeries'), data: dailyTrend.map((d) => d.values.count), color: colors.dispatch },
                { label: t('dispatchPanel.issuedQty'), data: dailyTrend.map((d) => d.values.issuedQty), color: colors.overview },
              ]}
              legendPosition="bottom"
              showValueLabels
            />
          </ChartCard>
        </GridItem>
        <GridItem xs={12} md={5}>
          <ChartCard title={t('dispatchPanel.statusTitle')} loading={isLoading} empty={!isLoading && statusDistribution.length === 0}>
            <DashboardPieChart
              data={statusDistribution.map((s, i) => ({ label: s.key, value: s.count, color: STATUS_SLICE_COLORS[i % STATUS_SLICE_COLORS.length] }))}
              legendPosition="bottom"
            />
          </ChartCard>
        </GridItem>
      </GridContainer>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {t('dispatchOrdersPage.title')}
        </Typography>
        <PortalDateRangeFilter fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate} />
        <DataTable
          columns={columns}
          rows={withId(tableRows, (r) => `${r.dispatchOrderId}-${r.materialCode}`)}
          loading={isLoading}
          searchable
          exportFileName="dispatch-orders"
        />
      </Paper>
    </Stack>
  );
}
